import { IConfiguration } from '../../domain/conf'
import { Utils } from '../../utils/common'
import { logger } from '../../utils/common/logger'
import { DOMUtils } from '../../utils/dom'
import { GlobalStore } from '../../utils/storage/global'
import { Store } from '../../utils/storage/keys'
import { LocalStore } from '../../utils/storage/local'
import { AdDOM } from '../ads/ad'
import { IdentityEvent, IIdentityEvent } from '../common/events/identity'
import { IProfile, Profile } from '../common/profile'
import {
    IIdentityProviderConfig,
    IIdentityServiceAdapter,
    ISubscriberProviderConfig,
    ISubscriberServiceAdapter
} from './adapters/adapter'
import { ExtendedAccessService } from './adapters/google'
import { ProfileService } from './adapters/profile-service'
import { Viafoura } from './adapters/viafoura'

export interface IIdentityService {
    initialize(...args: any[]): void

    update(profile: IProfile, store: boolean): void
}

export class IdentityService {
    /**
     * Required DOM selectors and attributes.
     *
     * IMPORTANT: These will be duplicated elsewhere (templates, css) so ensure changes are coordinated
     * and instances minimized.
     */
    public static DOM = {
        attrs: {
            btns: 'data-sso-btns',
            close: 'data-id-close',
            loginRedirect: 'data-id-login-redirect',
            marker: 'data-sso-marker',
            myAccountBtn: 'data-sso-my-account-control',
            name: 'data-id-name',
            offerId: 'data-id-offer',
            signIn: 'data-id-login',
            signOut: 'data-id-logout',
            termId: 'data-id-term'
        },
        class: {
            adContainers: ['ad__container', 'vf3-conversations-list__promo'],
            buttonText: 'button__text',
            signedIn: 'sso-signedIn',
            signedOut: 'sso-signedOut',
            subscriptionAlert: 'tp-container-inner',
        },
        links: {},
        logo: '',
        property: ''
    }

    public profile: IProfile
    public profileService: ProfileService
    public subscriptionServiceEnabled: boolean

    protected hashCode: number
    protected hasSubscriptions: boolean
    protected hasRegistrantTerm: boolean
    protected hasRegistrantResId: boolean
    protected subscriptionClientId: string
    protected subscriptionAppId: string
    protected subscriptionRegistrantResId: string

    protected termId: string
    protected offerId: string
    protected subscribeButton: HTMLButtonElement

    protected openCheckoutAfterLogin: boolean
    protected conf: IConfiguration

    private identityProvider: IIdentityServiceAdapter
    private subscriberService: ISubscriberServiceAdapter
    private commentService: Viafoura
    private extendedAccessService: ExtendedAccessService

    /**
     * Adds DOM event listeners.
     */
    public constructor() {

        this.subscriptionServiceEnabled = false
        // fetch profile from per-domain store, proceed anonymously if none found
        this.profile = LocalStore.get<IProfile>(Store.ID) || new Profile()

        // attempt to fetch profile from global (cross-domain) store
        GlobalStore.get<Profile>(Store.ID)
            .then((profile) => {
                // update relevant data if same user
                if (profile && profile.email === this.profile.email) {
                    this.profile.ageOfMajority = profile.ageOfMajority
                    this.profile.id = profile.id
                }
            })
            .catch(console.log)

        document.addEventListener(IdentityEvent.IDENTITY_PROFILE_INIT, async (e: IIdentityEvent) => {
            logger('debug', 'Identity profile init event, updating profile')
            await this.update(e.detail)
        })
    }

    /**
     * Stores profile; global properties only
     * @param {Profile} profile - profile to store
     */
    public static async StoreProfile(profile: IProfile) {
        // clean
        const profileStore = Utils.prune(profile)

        // store
        LocalStore.set(Store.ID, profileStore)

        // subscriptions vary by property
        delete profileStore.subscriptions
        await GlobalStore.set(Store.ID, profileStore)
    }

    /**
     * Delete subscription alert message
     *
     * @params {string} className
     */
    private static removeSubscriptionAlert(className: string) {
        if (document.body.classList.contains(className)) {
            document.querySelector(`.${IdentityService.DOM.class.subscriptionAlert}`).remove()
            document.body.classList.remove(className)

            // let other UI elements know of removal
            window.dispatchEvent(
                new CustomEvent('articleLimitAlertEvent', {
                    detail: {
                        limit: false
                    }
                })
            )
        }
    }

    /**
     * Configure Identity Service
     *
     * @param conf - Configuration object from page-data
     * @param identityProvider - Identity Provider
     * @param subscriberService - Subscriber Service
     * @param commentService - Comment Service
     * @returns
     */
    public async initialize(conf: IConfiguration,
                            identityProvider: IIdentityServiceAdapter,
                            subscriberService?: ISubscriberServiceAdapter,
                            commentService?: Viafoura) {

        this.conf = conf

        this.identityProvider = identityProvider
        this.subscriberService = subscriberService
        this.commentService = commentService
        this.extendedAccessService = new ExtendedAccessService()
        await this.extendedAccessService.init()

        let ssoCreds: IIdentityProviderConfig
        let subscriberCreds: ISubscriberProviderConfig
        if (conf.identity) {
            ssoCreds = conf.identity.sso
            subscriberCreds = conf.identity.subscriber

            IdentityService.DOM.links = conf.identity.links
            IdentityService.DOM.logo = conf.identity.logo
            IdentityService.DOM.property = conf.identity.property
        }
        this.hasSubscriptions = !!subscriberCreds
        this.hasRegistrantTerm = this.hasSubscriptions ? !!subscriberCreds.registrantTermId : false
        this.subscriptionClientId = this.hasSubscriptions ? subscriberCreds.registrantTermId : ''
        this.subscriptionAppId = this.hasSubscriptions ? subscriberCreds.account : ''
        this.hasRegistrantResId = this.hasSubscriptions ? !!subscriberCreds.registrantResId : false
        this.subscriptionRegistrantResId = this.hasRegistrantResId ? subscriberCreds.registrantResId : ''

        if (this.subscriberService) {
            this.subscriptionServiceEnabled = true
        }

        if (ssoCreds) {

            document.addEventListener(IdentityEvent.COMMENT_LOGIN, (e: CustomEvent) => {
                // get commentRedirect from event detail
                const commentRedirect: boolean = e.detail.commentRedirect
                const commentParam = location.search.length > 0 ? '&comments' : '?comments'
                // redirect to comment widget if needed after sign in
                const redirectUrl = commentRedirect ? location.href + commentParam : location.href
                this.identityProvider.signIn(redirectUrl)
                logger('debug', `IdentityService Event COMMENT_LOGIN; redirect: ${commentRedirect}`)
            })
            document.addEventListener(IdentityEvent.COMMENT_LOGOUT, () => {
                this.identityProvider.signOut()
                logger('debug', `IdentityService Event COMMENT_LOGOUT`)
            })
            document.addEventListener(IdentityEvent.LOGIN_MODAL_READY, this.loginModalReady.bind(this))
            document.addEventListener(IdentityEvent.IDENTITY_CHANGE, async (e: IIdentityEvent) => {
                logger('debug', 'Identity change event, updating profile')
                await this.update(e.detail)
                // update UI
                logger('debug', 'Identity change event, updating UI')
                await this.updateUI()
            })

            await this.session()
            this.profileService = new ProfileService()
        } else {
            await this.update(this.profile)
        }

        if (this.hasSubscriptions) {
            logger('debug', 'IdentityService.initialize - property has subscriptions, updating subscriptions')
            await this.updateSubscriptions()
            logger('debug', 'IdentityService.initialize - updating conversions')
            await this.updateConversions()
        }

        // fire id init event
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_INIT, this.profile))
        await this.loginModalReady()
        logger('debug', 'IdentityService.initialize - updating UI')
        await this.updateUI()
        return this
    }

    public getProfile(): IProfile {
        return this.profile
    }

    public async update(data: IProfile) {

        logger('debug', 'IdentityService.update with incoming profile: ', data)
        logger('debug', 'IdentityService.update stored profile: ', data)

        // outdated data, exit
        if (data.updated && this.profile && data.updated < this.profile.updated) {
            logger('debug', 'IdentityService.update - outdated data, exiting')
            return
        }

        const newLogin = this.profile && (this.profile.email !== data.email)
        logger('debug', 'IdentityService.update - newLogin is ' + newLogin)

        if (newLogin) {
            this.profile = {...this.profile, conversions: undefined, subscriptions: undefined}
            logger('debug', 'IdentityService.update - conversions and subscriptions cleared')
        }

        // merge data
        this.profile = {...this.profile, ...data}
        const incomingHashCode = Utils.hashCode(Utils.prune(this.profile))

        // profile unchanged
        if (this.hashCode && (incomingHashCode === this.hashCode)) {
            logger('debug', 'IdentityService.update - profile unchanged, exiting')
            return
        }

        // update modified time
        if (this.hashCode || !this.profile.updated) {
            this.profile.updated = Date.now()
        }
        // update hash (need to set before async actions so can ignore when no changes)
        this.hashCode = incomingHashCode
        if (this.hasSubscriptions) {
            logger('debug', 'IdentityService.update - property has subscriptions, updating subscriptions')
            // get subscriptions
            await this.updateSubscriptions()
            logger('debug', 'IdentityService.update - subscriptions updated')

            // check for one-digital conversions (only once to avoid potential race condition adding double terms)
            if (newLogin) {
                logger('debug', 'IdentityService.update - new login, updating conversions')
                await this.updateConversions()
                logger('debug', 'IdentityService.update - conversions updated')
            }
        }

        // update hash
        this.profile.updated = Date.now()
        this.hashCode = Utils.hashCode(Utils.prune(this.profile))

        if (this.profile.email && newLogin) {
            document.dispatchEvent(
                new CustomEvent(IdentityEvent.NEW_LOGIN, {
                    detail: {
                        data: this.profile
                    }
                })
            )

            if (this.openCheckoutAfterLogin && this.offerId && this.termId) {
                this.subscriberService.showCheckoutModal(this.offerId, this.termId, this.subscribeButton)
                this.openCheckoutAfterLogin = false
            }
        }

        // store
        await IdentityService.StoreProfile(this.profile)
        document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_READY, {detail: this.profile}))
    }

    /**
     * Enable sign-in link when adaptor is ready
     */
    public async loginModalReady() {
        await DOMUtils.ready

        /* register listeners only when janrain finished loading */
        this.addListeners()

        /* remove sign in loader */
        const signInEl: NodeListOf<HTMLButtonElement> = document.querySelectorAll(
            `[${IdentityService.DOM.attrs.signIn}]`
        )
        if (signInEl.length && signInEl.length > 0) {
            signInEl[0].removeAttribute('style')
            signInEl[0].classList.add('signin-button--modal-ready')
            signInEl[0].setAttribute('aria-disabled', 'false')
            signInEl[0].disabled = false
            const loaderDiv = document.getElementsByClassName('signInLoader')
            if (loaderDiv.length && loaderDiv.length > 0) {
                signInEl[0].removeChild(loaderDiv[0])
            }
        }
    }

    /**
     * Check if subscribed to the property (Not cross-app access)
     */
    public subscribed() {
        const propertySubscriber = (this.profile.subscriptions || []).filter((subscription) => {
            return subscription.aid === this.subscriptionAppId && subscription.id !== this.subscriptionRegistrantResId
        })
        return (propertySubscriber.length > 0)
    }

    /**
     * Updates UI (buttons etc.). Called after state change etc.
     */
    public async updateUI() {
        await DOMUtils.ready
        logger('info', 'updating UI')

        const adLight = this.profile.adLight
        const subscribed = this.subscribed()
        logger('info', 'subscribed: ', subscribed)

        if (adLight) {
            this.commentService.removeCommentWidgetsAdUI()
            this.removeAdSlotElements()
        }

        this.setName(this.profile.email ? this.profile.name : undefined).catch(console.log)

        if (this.signedIn()) {
            // Remove the article counter
            if (adLight || subscribed) {
                IdentityService.removeSubscriptionAlert('article-counter--visible')
            }

            // Remove the register counter
            IdentityService.removeSubscriptionAlert('register-counter--visible')

            // Remove register hard gate.
            // The .register-modal--visible class is coming from Piano admin panel.
            // Don't need to check if subscription services is enabled or not
            if (document.body.classList.contains('register-modal--visible')) {
                document.body.classList.remove('register-modal--visible')
                this.subscriberService.closeCheckoutModal()
            }

            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}]`), -1)
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signOut}]`))

            // signed in unhide the my account element from DOM & AT
            const myAccountBtn = document.querySelector<HTMLButtonElement>(
                `[${IdentityService.DOM.attrs.myAccountBtn}]`)
            if (myAccountBtn) { myAccountBtn.hidden = false }

            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.myAccountBtn}]`))
        } else {
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signIn}]`))
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.signOut}]`), -1)
            DOMUtils.updateTabs(document.querySelectorAll(
                `[${IdentityService.DOM.attrs.myAccountBtn}]`), -1)
        }
        if (document.querySelectorAll(`[${IdentityService.DOM.attrs.marker}]`).length) {
            // find DOM items based on marker specified above
            // replace class show/hide for animation rather than display block/none
            DOMUtils.nodes(document.querySelectorAll(`[${IdentityService.DOM.attrs.marker}], body`))
                .forEach((el: Element) => {
                    // replace signed in classes based on status
                    if (!this.signedIn()) {
                        DOMUtils.replaceClass(el,
                            IdentityService.DOM.class.signedIn,
                            IdentityService.DOM.class.signedOut)
                    } else if (!!this.signedIn()) {
                        DOMUtils.replaceClass(el,
                            IdentityService.DOM.class.signedOut,
                            IdentityService.DOM.class.signedIn)
                    }
                })
        } else {
            // display block/none method currently used
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.signIn}]`),
                !this.signedIn())
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.signOut}]`),
                !!this.signedIn())
            DOMUtils.setVis(
                document.querySelectorAll(
                    `[${IdentityService.DOM.attrs.myAccountBtn}]`),
                !!this.signedIn())
        }
    }

    /**
     * Handles session init event from SSO provider.
     */
    protected async session() {
        const profile = this.profile
        logger('info', 'identity session called with profile:', profile)

        // tslint:disable-next-line:max-line-length
        await this.commentService.initialize(this.conf.ads, this.identityProvider.getAccessToken())
        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_SESSION_PROFILE_INIT, profile))
        await this.update(profile)
    }

    /**
     * Registers listeners for required DOM events.
     *
     * TODO: expand as implementation requires.
     * @TODO: rename this
     */
    private addListeners() {
        // #if(document){ return }
        document.addEventListener('click', async (e: Event) => {
            let el = e.target as Element

            if (el.classList.contains(IdentityService.DOM.class.buttonText)) {
                // move up to the button element if clicking on the button text
                el = el.parentElement
            }

            const lrTokenKey = localStorage.getItem(Store.LR_TOKEN_KEY)
            const redirectUrl = location.origin + el.getAttribute(IdentityService.DOM.attrs.loginRedirect)

            // Use "data-id-login-redirect" to make custom redirect from button
            if (el.hasAttribute(IdentityService.DOM.attrs.loginRedirect) && !lrTokenKey) {
                this.identityProvider.signIn(redirectUrl)
            } else if (el.hasAttribute(IdentityService.DOM.attrs.loginRedirect) && lrTokenKey) {
                location.assign(redirectUrl)
            }

            if (el.hasAttribute(IdentityService.DOM.attrs.signIn)) {
                this.openCheckoutAfterLogin = false
                this.identityProvider.signIn()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.signOut)) {
                if (this.commentService) {
                    logger('info', 'identity sign out called - signing out vf widget')
                    await this.commentService.doWidgetSignout()
                    logger('info', 'widget signed out successfully')
                }
                logger('info', 'identity sign out called - signing out FEM')
                this.identityProvider.signOut()
            } else if (el.hasAttribute(IdentityService.DOM.attrs.offerId) && this.subscriberService) {
                this.handleOfferButton(el as HTMLButtonElement)
            }
        })
    }

    /**
     * Offer button interactions
     *
     * @param {HTMLButtonElement} button
     */
    private handleOfferButton(button: HTMLButtonElement) {
        this.offerId = button.getAttribute(IdentityService.DOM.attrs.offerId)
        this.termId = button.getAttribute(IdentityService.DOM.attrs.termId)
        if (this.offerId && this.termId) {
            this.subscriberService.showCheckoutModal(this.offerId, this.termId, button)
        }
    }

    /**
     * Sets name in UI (based on attr). Hides for anonymous users.
     *
     * @param {string} name
     */
    private async setName(name?: string) {
        await DOMUtils.ready
        DOMUtils.nodes(document.querySelectorAll(`[${IdentityService.DOM.attrs.name}]`))
            .forEach((el: Element) => {
                DOMUtils.setVis(el, !!name)
                el.innerHTML = name
            })
    }

    /**
     * If one digital, checks to see if user needs to be added to registrant term.
     */
    private async updateConversions() {
        if (this.profile.email) {
            if (this.profile.conversions === undefined && this.hasRegistrantTerm) {
                const conversions = await this.subscriberService.getConversions()
                this.profile.conversions = conversions
                logger('debug', 'IdentityService.updateConversions - conversions:', conversions)

                if (this.profile.conversions && this.profile.conversions.indexOf(this.subscriptionClientId) === -1) {
                    // no record of having registered this user for basic digital access on this one-digital property
                    // tslint:disable-next-line: max-line-length
                    logger('debug', 'IdentityService.updateConversions - registering for One digital access')
                    // tslint:disable-next-line: max-line-length
                    await this.subscriberService.registerForOneDigital(this.conf.identity.subscriber.account)
                    // tslint:disable-next-line: max-line-length
                    logger('debug', 'IdentityService.updateConversions - registered for One digital access')
                    this.profile.conversions = [...this.profile.conversions, this.subscriptionClientId]
                }
            }
        } else {
            logger('debug', 'IdentityService.updateConversions - no email, not updating conversions')
            this.profile.conversions = undefined
        }
    }

    /**
     * Gets subscriptions.
     */
    private async updateSubscriptions() {
        if (this.profile.email) {
            if (this.profile.subscriptions === undefined && this.hasSubscriptions) {

                // direct assignment causes issues in Mac FF/Saf (others?):
                // this.profile.subscriptions = await this.adapter.getAccess()

                // ...but intermediate assignment works:

                const access = await this.subscriberService.getAccess()
                this.profile.subscriptions = access
                logger('debug', 'IdentityService - updateSubscriptions - Subscriptions: ', access)
            }
        } else {
            logger('debug', 'IdentityService - updateSubscriptions: no email, not updating subscriptions')
            this.profile.subscriptions = undefined
        }
    }

    /**
     * Check if user is currently signed in
     */
    private signedIn() {
        return this.profile.email ? true : undefined
    }

    /**
     * Retrieve all ad slots by the container from DOM and delete
     * leaving graphical line separator from the ad section.
     * @TODO move this to viafoura object
     */
    private removeAdSlotElements() {
        for (const adContainer of IdentityService.DOM.class.adContainers) {
            const adSlots = Array.from(document.querySelectorAll(`.${adContainer}`))
                .filter((el: Element) =>
                    !el.querySelector(`[${AdDOM.attr.adLightIneligible.key}]`)
                )
            adSlots.forEach((el: Element) => el.remove())
        }
    }
}
